import React, { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FormControl,
  FormGroup,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Box,
  Button,
  IconButton
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import { ConfirmDialogContext } from '@seccon/core/contexts/confirm';
import FormFooter from './FormFooter';
import DetailPanelContent from './DetailPanelContent';
import useOverviewData from '../hooks/useOverviewData';
import useCreateSelectedPersonnel from '../hooks/useCreateSelectedPersonnel';
import useUpdateSelectedPersonnel from '../hooks/useUpdateSelectedPersonnel';

const STATUS_ALL = 'All';
const STATUS_MATCHED = 'MATCHES';
const STATUS_UPDATE = 'NEEDS_VERIFICATION';
const STATUS_MISSING = 'MISSING';

const EMPTY_ARRAY = [];

const Overview = () => {
  const overviewDataGridref = useGridApiRef();
  const { id: sessionId } = useParams();
  const confirm = React.useContext(ConfirmDialogContext);
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const onPencilClick = (mappedId) => {
    setSearchParams((state) => {
      state.set('tab', 'updateRecords');
      state.set('mappedId', mappedId);

      return state;
    });
  }
  const [displayCreateButton, setDisplayCreateButton] = useState(false);
  const [displayUpdateButton, setDisplayUpdateButton] = useState(false);
  const [selectedOption, setSelectedOption] = useState(STATUS_ALL);

  const { data, isLoading, isError } = useOverviewData(sessionId);
  const matchedCount = data?.totals?.matchingRecords ?? 0;
  const updateCount = data?.totals?.recordsToVerify ?? 0;
  const missingCount = data?.totals?.missingRecords ?? 0;
  const allCount = matchedCount + updateCount + missingCount;
  const personnelFromSystem = data?.personnelFromSystem ?? EMPTY_ARRAY;

  const filteredData = useMemo(() => (selectedOption === STATUS_ALL ? personnelFromSystem : personnelFromSystem.filter((record) => record.status === selectedOption)));

  const updateMutation = useUpdateSelectedPersonnel();

  const createMutation = useCreateSelectedPersonnel();

  const handleBulkUpdate = async (selectedMappedIds) => {
    await updateMutation.mutateAsync({ sessionId, selectedMappedIds });
    overviewDataGridref.current.setSelectionModel([]);
  };

  const handleBulkCreate = async (selectedMappedIds) => {
    await createMutation.mutateAsync({ sessionId, selectedMappedIds });
    overviewDataGridref.current.setSelectionModel([]);
  };

  const handleUpdateRecords = async () => {
    if (!overviewDataGridref.current) return;
    if (!overviewDataGridref.current.getSelectedRows()) return;

    const selectedMappedIds = Array.from(overviewDataGridref.current.getSelectedRows().values())
      .filter((row) => row.status === STATUS_UPDATE)
      .map((row) => row.mappedId);

    confirm(
      'Update Records',
      `This action will overwrite all the fields of ${ selectedMappedIds.length } personnel record in security control. Are you sure you want to proceed?`,
      () => handleBulkUpdate(selectedMappedIds)
    );
  };

  const handleCreateRecords = async () => {
    if (!overviewDataGridref.current) return;
    if (!overviewDataGridref.current.getSelectedRows()) return;

    const selectedMappedIds = Array.from(overviewDataGridref.current.getSelectedRows().values())
      .filter((row) => row.status === STATUS_MISSING)
      .map((row) => row.mappedId);

    confirm(
      'Create Records',
      `This action will create ${ selectedMappedIds.length } personnel record in security control. Are you sure you want to proceed?`,
      () => handleBulkCreate(selectedMappedIds)
    );
  };

  const handleStatusChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const handleSelectionChange = () => {
    if (!overviewDataGridref.current) return;
    if (!overviewDataGridref.current.getSelectedRows()) return;

    const { missingSelected, updateSelected } = Array.from(overviewDataGridref.current.getSelectedRows().values())
      .reduce((result, row) => ({
        missingSelected: result.missingSelected || row.status === STATUS_MISSING,
        updateSelected: result.updateSelected || row.status === STATUS_UPDATE
      }), { missingSelected: false, updateSelected: false });

    setDisplayCreateButton(missingSelected && !updateSelected);
    setDisplayUpdateButton(updateSelected && !missingSelected);
  };

  const getDetailPanelContent = React.useCallback(
    ({ row }) => <DetailPanelContent row={row} />,
    []
  );

  const getDetailPanelHeight = React.useCallback(() => 'auto', []);

  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const statusColors = {
          MATCHES: 'success.main',
          NEEDS_VERIFICATION: 'warning.main',
          MISSING: 'error.main'
        };
        const statusLabels = {
          [STATUS_MATCHED]: 'MATCHED',
          [STATUS_UPDATE]: 'UPDATE',
          [STATUS_MISSING]: 'MISSING'
        }
        return (
          <Box display='flex' alignItems='center'>
            <Typography sx={{ fontWeight: 'bold', color: statusColors[params.value] }}>
              {statusLabels[params.value] ?? params.value}
            </Typography>
            {(params.value === STATUS_UPDATE) && (
              <IconButton sx={{ margin: 0.5 }} onClick={() => onPencilClick(params.id)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
        );
      }
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'ssn', headerName: 'Social Security', flex: 1 },
    { field: 'smo', headerName: 'SMO', flex: 1 },
    { field: 'eligibility', headerName: 'Eligibility', flex: 1 },
    { field: 'access', headerName: 'Access', flex: 1 },
    { field: 'relationship', headerName: 'Relationship', flex: 1 }
  ];

  if (isLoading || isError) return null;

  return (
    <>
      <FormControl component='fieldset'>
        <FormGroup row>
          <RadioGroup row value={selectedOption} onChange={handleStatusChange}>
            <FormControlLabel
              value={STATUS_ALL}
              control={<Radio />}
              label={`All (${ allCount })`}
            />
            <FormControlLabel
              value={STATUS_MATCHED}
              control={<Radio />}
              label={`Matched (${ matchedCount })`}
            />
            <FormControlLabel
              value={STATUS_UPDATE}
              control={<Radio />}
              label={`Update (${ updateCount })`}
            />
            <FormControlLabel
              value={STATUS_MISSING}
              control={<Radio />}
              label={`Missing (${ missingCount })`}
            />
          </RadioGroup>
        </FormGroup>
      </FormControl>
      <DataGridPro
        apiRef={overviewDataGridref}
        columns={columns}
        rows={filteredData}
        getRowId={(row) => row.mappedId}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={(params) => params.row.status !== 'MATCHES'}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={getDetailPanelContent}
        initialState={{
          pagination: {
            pageSize: 10,
            page: 0
          }
        }}
        autoHeight
        sx={{ marginTop: 2 }}
        onSelectionModelChange={handleSelectionChange}
      />
      <FormFooter>
        {displayUpdateButton && (
          <Button onClick={handleUpdateRecords} color='primary' variant='contained' sx={{ minWidth: '100px' }}>
            Update
          </Button>
        )}{displayCreateButton && (
          <Button onClick={handleCreateRecords} color='primary' variant='contained' sx={{ minWidth: '100px' }}>
            Create
          </Button>
        )}
      </FormFooter>
    </>
  );
}

export default Overview;
